// Builds the "VPF Calculator Print Out" PDF payload from the LIVE calculator
// state. Values are formatted here exactly as they appear on screen so the
// generated PDF matches the Calculator page 1:1. The backend is a pure layout
// engine (POST /api/calculator_pdf).

import type { CalcInputs, CalcResult, VPFDimensions } from "@/types/calc";
import { fmt, fmtInt, fmtSmart, fmtRecirc } from "@/lib/format";
import { calculate, buildVPF } from "@/lib/api";
import { FALLBACK_INPUTS, coerceCalcInputs } from "@/lib/validators";
import { CALC_KEY } from "@/components/gantt/ganttTypes";
import { formatBlockSize, formatQty, siInputToUS, unitLabel } from "@/lib/units";
import type { Quantity, UnitSystem } from "@/lib/units";

export interface PdfRow { label: string; value: string; unit?: string; }
export interface PdfSection { title: string; rows: PdfRow[]; }
/** One phase of a single freezer cycle, for the Print Out's stacked cycle bar. */
export interface PdfCycleSeg {
  key: "load" | "freeze" | "defrost" | "unload" | "wait";
  label: string;
  minutes: number;
}
export interface CalculatorPdfPayload {
  model_code: string;
  n_freezers: string;
  left: PdfSection[];
  right: PdfSection[];
  cycle: PdfCycleSeg[];
}

/**
 * One cycle split into phases. The engine's tUnload lumps defrost, open,
 * unload AND the wait / misc allowance together, which can make unloading
 * look far longer than loading — so defrost and wait are split back out.
 */
function cycleSegments(r: any): PdfCycleSeg[] {
  const v = (x: unknown) => (typeof x === "number" && isFinite(x) ? Math.max(0, x) : 0);
  const defrost = v(r?.defrosting);
  const wait = v(r?.waitMisc);
  const segs: PdfCycleSeg[] = [
    { key: "load", label: "Loading", minutes: v(r?.tLoad) },
    { key: "freeze", label: "Freezing", minutes: v(r?.estFreezingTime) },
    { key: "defrost", label: "Defrost", minutes: defrost },
    { key: "unload", label: "Unloading", minutes: Math.max(0, v(r?.tUnload) - defrost - wait) },
    { key: "wait", label: "Wait / Misc", minutes: wait },
  ];
  return segs.filter(s => s.minutes > 0);
}

const DASH = "\u2014";
const clean = (v: unknown) => String(v ?? "").replace(/!/g, "").trim();
const num = (v: number | null | undefined, d = 1) =>
  v === null || v === undefined || Number.isNaN(v) ? DASH : fmtSmart(v, d);
const int = (v: number | null | undefined) =>
  v === null || v === undefined || Number.isNaN(v) ? DASH : fmtInt(v);

/**
 * `units` is DISPLAY ONLY (Settings → PDF units): every value is still read
 * from the SI inputs/result and converted here, row by row. With "SI" the
 * payload is byte-identical to what it was before units existed.
 */
export function buildCalculatorPdfPayload(
  inputs: CalcInputs,
  r: CalcResult,
  vpf: VPFDimensions | null,
  units: UnitSystem = "SI",
): CalculatorPdfPayload {
  const isSecondary = inputs.refrigerantType === "Secondary";
  const us = units === "US";
  /** A stored SI input string (sans "!") in the PDF units. */
  const inp = (v: unknown, q: Quantity) => (us ? siInputToUS(clean(v), q) : clean(v));
  /** A computed SI value in the PDF units; SI keeps the existing formatter. */
  const out = (v: number | null | undefined, q: Quantity, siFormat: (x: number) => string) =>
    formatQty(v, q, units, siFormat);
  const u = (q: Quantity) => unitLabel(q, units);
  const num1 = (x: number) => num(x, 1);

  const htfOutlet = (() => {
    const inStr = clean(inputs.htfInletTemp);
    const dtStr = clean(inputs.htfDeltaT);
    if (inStr === "" || dtStr === "") return DASH;   // Number("") would be 0
    const inlet = Number(inStr);
    const delta = Number(dtStr);
    return !isFinite(inlet) || !isFinite(delta) ? DASH : out(inlet + delta, "temp", (x) => fmt(x, 1));
  })();

  // ── Refrigerant rows differ for Primary vs Secondary ──────────────────────
  const refrigerantRows: PdfRow[] = [
    { label: "Refrigerant Type", value: clean(inputs.refrigerantType) },
  ];
  if (isSecondary) {
    refrigerantRows.push(
      { label: "Heat Transfer Fluid", value: clean(inputs.heatTransferFluid) },
      { label: "Fluid Inlet Temp.", value: inp(inputs.htfInletTemp, "temp"), unit: u("temp") },
      { label: "Fluid \u0394T", value: inp(inputs.htfDeltaT, "tempDelta"), unit: u("tempDelta") },
      { label: "Fluid Outlet Temp.", value: htfOutlet, unit: u("temp") },
    );
  } else {
    refrigerantRows.push(
      { label: "Refrigerant", value: clean(inputs.refrigerant) },
      { label: "Evaporation Temp.", value: inp(inputs.evapTemp, "temp"), unit: u("temp") },
    );
  }

  // ── Est. Freezing Time / Defrosting honour the AUTO/MAN override ───────────
  const freezeVal = clean(inputs.overrideFreezeTime) !== ""
    ? clean(inputs.overrideFreezeTime)
    : int(r.estFreezingTimeCalc);
  const defrostVal = clean(inputs.overrideDefrostMin) !== ""
    ? clean(inputs.overrideDefrostMin)
    : num(r.defrosting, 1);

  const recircVal = isSecondary
    ? (us ? `${inp(inputs.htfDeltaT, "tempDelta")}\u00b0F \u0394T` : `${clean(inputs.htfDeltaT)}K \u0394T`)
    : fmtRecirc(r.recirculationRate);

  const left: PdfSection[] = [
    {
      title: "Product Properties",
      rows: [
        { label: "Product Name", value: clean(inputs.productName) },
        { label: "Thermal Properties", value: clean(inputs.thermalProperty) },
        { label: "Inlet Temperature", value: inp(inputs.inletTemp, "temp"), unit: u("temp") },
        { label: "Outlet Temperature", value: inp(inputs.outletTemp, "temp"), unit: u("temp") },
        { label: "Density", value: inp(inputs.density, "density"), unit: u("density") },
        { label: "Contact %", value: clean(inputs.contactPc), unit: "%" },
      ],
    },
    { title: "Refrigerant", rows: refrigerantRows },
    {
      title: "Freezer Sizing",
      rows: [
        { label: "No. of Freezers", value: clean(inputs.nFreezers) },
        { label: "No of Stations", value: clean(inputs.nStations) },
        { label: "Station Length", value: inp(inputs.lStation, "length"), unit: u("length") },
        { label: "Station Depth", value: inp(inputs.dStation, "length"), unit: u("length") },
        { label: "Station Thickness", value: inp(inputs.tStation, "length"), unit: u("length") },
        { label: "Dividers", value: clean(inputs.nDividers) },
      ],
    },
    {
      title: "Freezer Physical Dimensions",
      rows: [
        { label: "Freezer Length", value: vpf ? out(vpf.OALength, "length", fmtInt) : DASH, unit: u("length") },
        { label: "Freezer Depth", value: vpf ? out(vpf.OADepth, "length", fmtInt) : DASH, unit: u("length") },
        { label: "Freezer Height (Closed)", value: vpf ? out(vpf.OAHeightLowered, "length", fmtInt) : DASH, unit: u("length") },
        { label: "Freezer Height (Open)", value: vpf ? out(vpf.OAHeightRaised, "length", fmtInt) : DASH, unit: u("length") },
        { label: "Freezer Weight", value: "N/A" },
      ],
    },
  ];

  const right: PdfSection[] = [
    {
      title: "Freezer Product Load",
      rows: [
        { label: "Block Size", value: us ? formatBlockSize(r.blockSize, units) : (r.blockSize ?? DASH), unit: u("length") },
        { label: "Est. Block Weight", value: out(r.estBlockWeight, "mass", num1), unit: u("mass") },
        { label: "Blocks per Station", value: String(r.blocksPerStation ?? DASH) },
        { label: "Est. Station Weight", value: out(r.estStationWeight, "mass", num1), unit: u("mass") },
        { label: "Weight per Batch", value: out(r.weightPerBatch, "mass", num1), unit: u("mass") },
      ],
    },
    {
      title: "Freezing & Cycle Times",
      rows: [
        { label: "Time to close / lower", value: int(r.closingTime), unit: "sec" },
        { label: "Loading Time", value: clean(inputs.loadingTime), unit: "sec/stn" },
        { label: "Est. Freezing Time", value: freezeVal, unit: "min" },
        { label: "Defrosting", value: defrostVal, unit: "min" },
        { label: "Time to open / raise", value: int(r.openingTime), unit: "sec" },
        { label: "Unloading", value: clean(inputs.unloadingTime), unit: "sec/stn" },
        { label: "Wait / Misc", value: clean(inputs.waitMisc), unit: "min" },
        { label: "Total Loading Time", value: num(r.tLoad, 1), unit: "min" },
        { label: "Total Unloading Time", value: num(r.tUnload, 1), unit: "min" },
        { label: "Total Cycle Time", value: num(r.totalCycleTime, 1), unit: "min" },
      ],
    },
    {
      title: "Freezing / Throughput Capacity",
      rows: [
        { label: "Nominal Capacity each", value: out(r.nominalCapacityEach, "capacity", num1), unit: u("capacity") },
        { label: "Nominal Capacity Total", value: out(r.nominalCapacityTotal, "capacity", num1), unit: u("capacity") },
        { label: "\u2026per day, 24/7", value: out(r.perDay24_7, "throughput", fmtInt), unit: u("throughput") },
        { label: `${r.cyclesPer12Hrs ?? "#"} cycles per 12 h`, value: out(r.throughputPer12Hrs, "throughput", fmtInt), unit: u("throughput") },
        { label: `${r.cyclesPer24Hrs ?? "#"} cycles per 24 h`, value: out(r.throughputPer24Hrs, "throughput", fmtInt), unit: u("throughput") },
        { label: `${r.cyclesPerWindow ?? "#"} cycles per ${clean(inputs.cyclesWindowHrs)} h`, value: out(r.throughputPerWindow, "throughput", fmtInt), unit: u("throughput") },
      ],
    },
    {
      title: "Refrigeration Details",
      rows: [
        { label: "Cooling Load per Freezer", value: out(r.coolingLoadPerFreezer, "power", num1), unit: u("power") },
        { label: "Est. Overall Cooling Load", value: out(r.overallCoolingLoad, "power", num1), unit: u("power") },
        { label: "Recirculation Rate", value: recircVal },
        { label: "Max. Defrost Capacity", value: out(r.maxDefrostCapacity, "power", num1), unit: u("power") },
        { label: "Peak Cooling Load", value: out(r.peakCoolingLoad, "power", num1), unit: u("power") },
        { label: isSecondary ? "Fluid Mass Flow" : "Ref. Mass Flow", value: out(r.refMassFlow, "massFlow", fmtInt), unit: u("massFlow") },
        { label: isSecondary ? "Fluid Vol. Flow" : "Ref. Volume Flow", value: out(r.refVolumeFlow, "volFlow", fmtInt), unit: u("volFlow") },
        { label: "Internal Volume", value: vpf ? out(vpf.InternalVolume, "volume", fmtInt) : DASH, unit: u("volume") },
      ],
    },
  ];

  return {
    model_code: r.modelCode ?? (vpf?.modelCode ?? ""),
    n_freezers: clean(inputs.nFreezers),
    left,
    right,
    cycle: cycleSegments(r),
  };
}

// ── Live builder (single source of truth for BOTH pages) ───────────────────
// Reads the persisted Calculator inputs and recomputes the result + VPF
// geometry fresh from the backend, so the Print Out / Combined Report are
// accurate and identical whether triggered from the Calculator or the Gantt.

function mapVpf(result: any): VPFDimensions | null {
  const v = result?.vpf;
  if (!v) return null;
  return {
    OALength: v.OALength, OADepth: v.OADepth,
    OAHeightLowered: v.OAHeightLowered, OAHeightRaised: v.OAHeightRaised,
    InternalVolume: v.InternalVolume, lStackClosed: v.lStackClosed,
    lStackOpen: v.lStackOpen, mPlates: v.mPlates, pPressure: v.pPressure,
    modelCode: v.modelCode, valid: result.valid ?? true, errors: result.errors ?? [],
  };
}

export async function buildCalculatorPdfPayloadLive(
  units: UnitSystem = "SI",
): Promise<CalculatorPdfPayload> {
  let inputs: CalcInputs = FALLBACK_INPUTS;
  try {
    const raw = localStorage.getItem(CALC_KEY);
    if (raw) inputs = { ...FALLBACK_INPUTS, ...coerceCalcInputs(JSON.parse(raw)) };
  } catch {/* ignore */}

  const [resp, vpfResp] = await Promise.all([
    calculate(inputs),
    buildVPF(inputs).catch(() => null),
  ]);
  if (!resp.ok || !resp.result) {
    throw new Error("Calculation failed — cannot build the Calculator Print Out.");
  }
  const vpf = vpfResp?.ok ? mapVpf(vpfResp.result) : null;
  return buildCalculatorPdfPayload(inputs, resp.result, vpf, units);
}
